<template>
  <div class="payments">
    <n-card>
      <template #header>
        <div class="card-header">
          <span>All Payments</span>
          <n-space>
            <n-select
              v-model:value="filters.payment_method"
              :options="paymentMethodOptions"
              placeholder="Payment Method"
              clearable
              style="width: 180px"
            />
            <n-date-picker
              v-model:value="filters.dateRange"
              type="daterange"
              clearable
              :shortcuts="dateShortcuts"
            />
          </n-space>
        </div>
      </template>

      <n-data-table
        :columns="columns"
        :data="payments"
        :loading="loading"
        :row-key="(row) => row.id"
        :summary="summary"
        :default-sort="{ columnKey: 'payment_date', order: 'descend' }"
      />
    </n-card>

    <!-- Record Payment Modal -->
    <n-modal v-model:show="showRecordModal" preset="dialog" title="Record Payment">
      <n-form :model="paymentForm" label-placement="left" label-width="120">
        <n-form-item label="License ID">
          <n-input-number v-model:value="paymentForm.license_id" :min="1" />
        </n-form-item>
        <n-form-item label="Amount">
          <n-input-number v-model:value="paymentForm.amount" :min="0" :precision="2" />
        </n-form-item>
        <n-form-item label="Payment Date">
          <n-date-picker v-model:value="paymentForm.payment_date" type="date" />
        </n-form-item>
        <n-form-item label="Payment Method">
          <n-select
            v-model:value="paymentForm.payment_method"
            :options="paymentMethodOptions"
            placeholder="Select method"
          />
        </n-form-item>
        <n-form-item label="Notes">
          <n-input v-model:value="paymentForm.notes" type="textarea" :rows="2" />
        </n-form-item>
      </n-form>
      <template #action>
        <n-button @click="showRecordModal = false">Cancel</n-button>
        <n-button type="primary" :loading="saving" @click="handleRecordPayment">
          Record Payment
        </n-button>
      </template>
    </n-modal>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, h } from 'vue'
import {
  NCard,
  NDataTable,
  NSelect,
  NSpace,
  NTag,
  NButton,
  NModal,
  NForm,
  NFormItem,
  NDatePicker,
  NInputNumber,
  NInput,
} from 'naive-ui'
import { useToast } from '@shared/composables/useToast.js'
import { useAdmin } from '../composables/useAdmin'
import dayjs from 'dayjs'

const toast = useToast()
const { listPayments, recordPayment, deletePayment, loading } = useAdmin()

const payments = ref([])
const showRecordModal = ref(false)
const saving = ref(false)

const filters = ref({
  payment_method: null,
  dateRange: null,
})

const paymentForm = ref({
  license_id: null,
  amount: 0,
  payment_date: Date.now(),
  payment_method: 'cash',
  notes: '',
})

const paymentMethodOptions = [
  { label: 'Cash', value: 'cash' },
  { label: 'Bank Transfer', value: 'bank_transfer' },
  { label: 'Cheque', value: 'cheque' },
  { label: 'UPI', value: 'upi' },
  { label: 'Online', value: 'online' },
]

const dateShortcuts = {
  'Last 7 Days': () => {
    const end = Date.now()
    const start = dayjs().subtract(7, 'day').valueOf()
    return [start, end]
  },
  'Last 30 Days': () => {
    const end = Date.now()
    const start = dayjs().subtract(30, 'day').valueOf()
    return [start, end]
  },
  'This Month': () => {
    const start = dayjs().startOf('month').valueOf()
    const end = dayjs().endOf('month').valueOf()
    return [start, end]
  },
  'Last Month': () => {
    const start = dayjs().subtract(1, 'month').startOf('month').valueOf()
    const end = dayjs().subtract(1, 'month').endOf('month').valueOf()
    return [start, end]
  },
}

const columns = [
  { title: 'ID', key: 'id', width: 60, sorter: 'default' },
  { title: 'Business', key: 'business_name', sorter: 'default' },
  { title: 'Module', key: 'module_name', sorter: 'default' },
  {
    title: 'Amount',
    key: 'amount',
    sorter: (a, b) => a.amount - b.amount,
    render: (row) => formatCurrency(row.amount),
  },
  {
    title: 'Payment Date',
    key: 'payment_date',
    sorter: (a, b) => new Date(a.payment_date) - new Date(b.payment_date),
    render: (row) => dayjs(row.payment_date).format('MMM DD, YYYY'),
  },
  {
    title: 'Method',
    key: 'payment_method',
    sorter: 'default',
    render: (row) => {
      const labels = {
        cash: 'Cash',
        bank_transfer: 'Bank Transfer',
        cheque: 'Cheque',
        upi: 'UPI',
        online: 'Online',
      }
      return h(NTag, { size: 'small' }, () => labels[row.payment_method] || row.payment_method)
    },
  },
  {
    title: 'Recorded By',
    key: 'recorded_by_username',
    sorter: 'default',
  },
  {
    title: 'Actions',
    key: 'actions',
    render: (row) => {
      return h(
        NButton,
        {
          size: 'small',
          type: 'error',
          secondary: true,
          onClick: () => handleDeletePayment(row),
        },
        () => 'Delete'
      )
    },
  },
]

const summary = computed(() => {
  if (payments.value.length === 0) return undefined
  const total = payments.value.reduce((sum, p) => sum + (p.amount || 0), 0)
  return () => [
    h(
      'tr',
      { style: 'background: #fafafa; font-weight: bold' },
      [
        h('td', { colspan: 3 }, 'Total'),
        h('td', formatCurrency(total)),
        h('td', { colspan: 4 }),
      ]
    ),
  ]
})

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
  }).format(value || 0)
}

const handleRecordPayment = async () => {
  saving.value = true
  try {
    await recordPayment({
      license_id: paymentForm.value.license_id,
      amount: paymentForm.value.amount,
      payment_date: dayjs(paymentForm.value.payment_date).format('YYYY-MM-DD'),
      payment_method: paymentForm.value.payment_method,
      notes: paymentForm.value.notes,
    })
    toast.success('Payment recorded successfully')
    showRecordModal.value = false
    loadPayments()
  } catch (error) {
    toast.error(error.message)
  } finally {
    saving.value = false
  }
}

const handleDeletePayment = async (payment) => {
  if (!confirm(`Are you sure you want to delete this payment of ${formatCurrency(payment.amount)}?`)) {
    return
  }
  try {
    await deletePayment(payment.id)
    toast.success('Payment deleted successfully')
    loadPayments()
  } catch (error) {
    toast.error(error.message)
  }
}

const loadPayments = async () => {
  try {
    const params = {}
    if (filters.value.payment_method) {
      params.payment_method = filters.value.payment_method
    }
    if (filters.value.dateRange) {
      params.start_date = dayjs(filters.value.dateRange[0]).format('YYYY-MM-DD')
      params.end_date = dayjs(filters.value.dateRange[1]).format('YYYY-MM-DD')
    }

    const response = await listPayments(params)
    payments.value = response.payments || []
  } catch (error) {
    console.error('Failed to load payments:', error)
  }
}

watch(filters, loadPayments, { deep: true })

onMounted(loadPayments)
</script>

<style scoped>
.payments {
  max-width: 1400px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
